import React from 'react'
import {
  CheckCircleIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  NoSymbolIcon,
  PaperAirplaneIcon,
  ShieldExclamationIcon,
  TrophyIcon,
  UserGroupIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { Heading, LoadingSpinner, Text } from '../../components/ui'
import type { HelpdeskQuote } from '../../types/comercialCotacoes'
import {
  buildCotacoesDashboardMetrics,
  buildCotacoesVolumeSeries,
  getActiveVolumeStatusSeries,
  type CotacoesVolumeGranularity,
  type CotacoesVolumePoint,
  type DashboardStatusKey,
} from '../../utils/comercialCotacoesDashboard'
import {
  formatCurrencyBRL,
  formatIntegerPtBr,
  formatPercentPtBr,
  formatQuoteLabel,
  getClientDisplayName,
  quoteStatusDashboardStyle,
} from '../../utils/cotacaoFormatters'

type CotacoesDashboardTabProps = {
  quotes: HelpdeskQuote[]
  loading: boolean
  hasSearched: boolean
  dataInicio?: string
  dataFim?: string
  onQuoteClick?: (quote: HelpdeskQuote) => void
}

const STATUS_ICONS: Record<DashboardStatusKey, React.ComponentType<{ className?: string }>> = {
  expirada: ClockIcon,
  enviada: PaperAirplaneIcon,
  aprovada: CheckCircleIcon,
  recusada: XCircleIcon,
  'em analise': MagnifyingGlassIcon,
  cancelada: NoSymbolIcon,
  aberta: FolderIcon,
  bloqueada: ShieldExclamationIcon,
}

const GRANULARITY_OPTIONS: { id: CotacoesVolumeGranularity; label: string }[] = [
  { id: 'month', label: 'Mês' },
  { id: 'day', label: 'Dia' },
]

/** Visual alinhado ao Superapp (barras de total + linhas de status). */
const CHART_TOTAL = {
  bar: '#6366f1',
  label: '#4f46e5',
}

const STATUS_LINE_COLORS: Record<DashboardStatusKey, string> = {
  expirada: '#78716c',
  enviada: '#8b5cf6',
  aprovada: '#22c55e',
  recusada: '#ef4444',
  'em analise': '#f59e0b',
  cancelada: '#64748b',
  aberta: '#0ea5e9',
  bloqueada: '#f97316',
}

const TOOLTIP_CONTENT_STYLE: React.CSSProperties = {
  backgroundColor: '#1f2937',
  border: '1px solid #4b5563',
  borderRadius: 8,
  color: '#f3f4f6',
}

const TOOLTIP_LABEL_STYLE: React.CSSProperties = {
  color: '#d1d5db',
  fontWeight: 600,
  marginBottom: 4,
}

const formatLabelCount = (label: unknown): string => {
  const n = typeof label === 'number' ? label : Number(label)
  if (!Number.isFinite(n) || n <= 0) return ''
  return String(n)
}

const CotacoesDashboardTab: React.FC<CotacoesDashboardTabProps> = ({
  quotes,
  loading,
  hasSearched,
  dataInicio = '',
  dataFim = '',
  onQuoteClick,
}) => {
  const [granularity, setGranularity] = React.useState<CotacoesVolumeGranularity>('month')

  const metrics = React.useMemo(() => buildCotacoesDashboardMetrics(quotes), [quotes])

  const volumeSeries = React.useMemo(
    () =>
      buildCotacoesVolumeSeries(quotes, granularity, {
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
      }),
    [quotes, granularity, dataInicio, dataFim]
  )

  const activeStatusSeries = React.useMemo(
    () => getActiveVolumeStatusSeries(volumeSeries),
    [volumeSeries]
  )

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!hasSearched) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <Text size="sm" className="text-gray-500 dark:text-gray-400 text-center">
          Selecione ao menos um filtro e clique em Buscar para carregar os registros.
        </Text>
      </div>
    )
  }

  return (
    <div className="flex-1 px-6 py-3 space-y-3">
      <div className="flex flex-col xl:flex-row gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 xl:w-64 flex-shrink-0 flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40">
            <DocumentTextIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
          </div>
          <div className="min-w-0">
            <Text size="xs" className="text-gray-500 dark:text-gray-400 font-medium">
              Total de Cotações
            </Text>
            <Text as="p" size="3xl" weight="bold" className="text-gray-900 dark:text-white leading-none mt-0.5">
              {formatIntegerPtBr(metrics.totalCount)}
            </Text>
            <Text as="p" size="md" weight="semibold" className="text-green-600 dark:text-green-400 mt-1">
              Vidas: {formatIntegerPtBr(metrics.totalLives)}
            </Text>
            <Text as="p" size="md" weight="semibold" className="text-green-600 dark:text-green-400 mt-0.5">
              Prêmio: {formatCurrencyBRL(metrics.totalPremium)}
            </Text>
          </div>
        </div>

        <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {metrics.byStatus.map((status) => {
            const Icon = STATUS_ICONS[status.key]
            const style = quoteStatusDashboardStyle(status.key)
            return (
              <div
                key={status.key}
                className="bg-white dark:bg-gray-800 rounded-xl shadow p-3 flex items-center gap-2.5"
              >
                <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${style.iconBg}`}>
                  <Icon className={`h-4 w-4 ${style.iconColor}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <Text size="xs" className="text-gray-500 dark:text-gray-400 font-medium truncate">
                      {status.label}
                    </Text>
                    <Text size="xs" className="text-gray-400 dark:text-gray-500 shrink-0">
                      {formatPercentPtBr(status.percent)}
                    </Text>
                  </div>
                  <Text as="p" size="xl" weight="bold" className="text-gray-900 dark:text-white leading-none">
                    {formatIntegerPtBr(status.count)}
                  </Text>
                  <Text as="p" size="xs" weight="medium" className={`mt-0.5 ${style.premiumColor}`}>
                    Vidas: {formatIntegerPtBr(status.lives)}
                  </Text>
                  <Text as="p" size="xs" weight="medium" className={`mt-0.5 ${style.premiumColor}`}>
                    Prêmio: {formatCurrencyBRL(status.premium)}
                  </Text>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40">
                <ChartBarIcon className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                Volume de cotações
              </Heading>
            </div>
            <Text size="sm" color="muted" className="mt-1">
              Barras: total no período. Linhas: volume por status.
            </Text>
          </div>
          <div
            className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 p-0.5 self-start"
            role="group"
            aria-label="Granularidade do gráfico"
          >
            {GRANULARITY_OPTIONS.map(({ id, label }) => {
              const active = granularity === id
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setGranularity(id)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                    active
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  {label}
                </button>
              )
            })}
          </div>
        </div>

        {volumeSeries.length === 0 ? (
          <Text size="sm" color="muted">
            Sem dados para o período.
          </Text>
        ) : (
          <div className="h-[28rem] min-h-[28rem] w-full min-w-0 text-slate-600 dark:text-slate-300 [&_.recharts-legend-item-text]:fill-current [&_.recharts-cartesian-axis-tick-value]:fill-current">
            <ResponsiveContainer width="100%" height="100%" minHeight={420}>
              <ComposedChart
                data={volumeSeries}
                margin={{ top: 28, right: 28, left: 8, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" strokeOpacity={0.35} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12, fill: 'currentColor' }}
                  axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                  tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                  angle={volumeSeries.length > 14 ? -35 : 0}
                  textAnchor={volumeSeries.length > 14 ? 'end' : 'middle'}
                  height={volumeSeries.length > 14 ? 64 : 40}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: 'currentColor' }}
                  axisLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                  tickLine={{ stroke: 'currentColor', strokeOpacity: 0.45 }}
                  allowDecimals={false}
                  width={44}
                  domain={[0, (dataMax: number) => Math.max(5, Math.ceil(dataMax * 1.1) || 5)]}
                />
                <RechartsTooltip
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null
                    const row = payload[0]?.payload as CotacoesVolumePoint | undefined
                    if (!row) return null
                    return (
                      <div
                        className="rounded-lg px-3 py-2 text-sm shadow-lg max-w-xs"
                        style={{ ...TOOLTIP_CONTENT_STYLE, padding: '10px 12px' }}
                      >
                        <div style={TOOLTIP_LABEL_STYLE}>{String(label)}</div>
                        <div className="mt-2 space-y-0.5" style={{ color: '#e5e7eb' }}>
                          {activeStatusSeries.map(({ statusKey, dataKey, label: statusLabel }) => {
                            const value = row[dataKey] ?? 0
                            return (
                              <div key={dataKey} style={{ color: STATUS_LINE_COLORS[statusKey] }}>
                                {statusLabel}: {formatIntegerPtBr(value)}
                              </div>
                            )
                          })}
                        </div>
                        <div
                          className="mt-2 border-t border-gray-600 pt-2"
                          style={{ ...TOOLTIP_LABEL_STYLE, fontSize: '0.8125rem' }}
                        >
                          Total: {formatIntegerPtBr(row.total)}
                        </div>
                      </div>
                    )
                  }}
                />
                <Bar
                  dataKey="total"
                  name="Total"
                  fill={CHART_TOTAL.bar}
                  fillOpacity={0.22}
                  stroke={CHART_TOTAL.bar}
                  strokeOpacity={0.45}
                  radius={[4, 4, 0, 0]}
                  maxBarSize={48}
                  isAnimationActive={false}
                >
                  <LabelList
                    dataKey="total"
                    position="top"
                    offset={6}
                    fill={CHART_TOTAL.label}
                    className="dark:[fill:#a5b4fc]"
                    fontSize={11}
                    fontWeight={600}
                    formatter={formatLabelCount}
                  />
                </Bar>
                {activeStatusSeries.map(({ statusKey, dataKey, label }) => {
                  const color = STATUS_LINE_COLORS[statusKey]
                  return (
                    <Line
                      key={dataKey}
                      type="monotone"
                      dataKey={dataKey}
                      name={label}
                      stroke={color}
                      strokeWidth={2.5}
                      connectNulls={false}
                      dot={{ r: 3, fill: color, stroke: '#f8fafc', strokeWidth: 1 }}
                      activeDot={{ r: 5, fill: color, stroke: '#f8fafc', strokeWidth: 2 }}
                      isAnimationActive={false}
                    />
                  )
                })}
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={{ paddingTop: 8, paddingBottom: 0, marginBottom: 0 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <TopTenCard
          title="Top 10 — Mais Vidas"
          icon={UserGroupIcon}
          empty={metrics.topByLives.length === 0}
          onQuoteClick={onQuoteClick}
          items={metrics.topByLives.map((quote, index) => ({
            key: quote.id,
            quote,
            rank: index + 1,
            primary: formatQuoteLabel(quote.quote_number, quote.version),
            secondary: getClientDisplayName(quote),
            metric: `${formatIntegerPtBr(quote.insured_count)} vidas`,
          }))}
        />
        <TopTenCard
          title="Top 10 — Maior Prêmio"
          icon={TrophyIcon}
          empty={metrics.topByPremium.length === 0}
          onQuoteClick={onQuoteClick}
          items={metrics.topByPremium.map((quote, index) => ({
            key: quote.id,
            quote,
            rank: index + 1,
            primary: formatQuoteLabel(quote.quote_number, quote.version),
            secondary: getClientDisplayName(quote),
            metric: formatCurrencyBRL(quote.total_premium ?? null),
          }))}
        />
      </div>
    </div>
  )
}

type TopTenItem = {
  key: string
  quote: HelpdeskQuote
  rank: number
  primary: string
  secondary: string
  metric: string
}

const TopTenCard: React.FC<{
  title: string
  icon: React.ComponentType<{ className?: string }>
  items: TopTenItem[]
  empty: boolean
  onQuoteClick?: (quote: HelpdeskQuote) => void
}> = ({ title, icon: Icon, items, empty, onQuoteClick }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4">
    <div className="flex items-center gap-2 mb-3">
      <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/40">
        <Icon className="h-4 w-4 text-green-600 dark:text-green-400" />
      </div>
      <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
        {title}
      </Heading>
    </div>
    {empty ? (
      <Text size="sm" color="muted">
        Sem dados
      </Text>
    ) : (
      <div className="space-y-0.5">
        {items.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => onQuoteClick?.(item.quote)}
            className="w-full flex items-center gap-2 rounded-md px-1.5 py-1 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
          >
            <Text size="sm" weight="semibold" className="text-gray-400 dark:text-gray-500 w-6 shrink-0">
              {item.rank}
            </Text>
            <div className="flex-1 min-w-0">
              <Text size="sm" weight="medium" className="text-gray-900 dark:text-white truncate">
                {item.primary}
              </Text>
              <Text size="xs" color="muted" className="truncate">
                {item.secondary}
              </Text>
            </div>
            <Text size="sm" weight="medium" className="text-gray-700 dark:text-gray-300 shrink-0">
              {item.metric}
            </Text>
          </button>
        ))}
      </div>
    )}
  </div>
)

export default CotacoesDashboardTab
